<!--
@Author: Ethan Dinnen
@Date:   2019-03-25 9:54:00
@Last Modified by:   Carmen Chow <carmen@hackcapital.com>
@Last Modified time: 2019-04-22 11:32:00
-->

<template lang="pug">
  div
    form
      input(
        data-cy="attachments"
        type="file"
        multiple
        ref="attachments"
        :id="inputId"
        @change="handleFiles"
        style="display: none;"
        :accept="allowedTypes.join(', ')"
      )
    attachment-chips.mt-2(
      :files="uploaded"
      :is-disabled="isDisabled"
      :showProgress="!isDisabled"
      @removeAttachment="handleRemoveAttachment"
    )
</template>

<script>
import { feathers } from '@/feathers-client';
import AttachmentChips from '@/Communications/components/AttachmentChips';

const MAX_FILE_SIZE_BYTES = 15728640;

export default {
  components: {
    AttachmentChips,
  },
  props: {
    inputId: {
      type: String,
      required: true,
    },
    files: {
      type: Array,
      required: true,
      default: () => [],
    },
    folderId: {
      type: Number,
      default: 0,
    },
    uploadedFiles: {
      type: Array,
      default: () => [],
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      uploaded: [],
      allowedTypes: [
        // Documents
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        // Powerpoint
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        // Videos
        'video/quicktime',
        'video/mp4',
        // Images
        'image/jpeg',
        'image/png',
        'image/gif',
      ],
    };
  },
  watch: {
    uploadedFiles: {
      handler(value) {
        this.uploaded = value;
      },
      deep: true,
    },
    files(value) {
      if (value.length) return;
      /**
       * Clear uploaded array of completed files if passed file array is empty
       * This keeps the component up to date when emails are sent as the files
       * store array is cleared on send
       */
      this.uploaded = this.uploaded.reduce((acc, file) => {
        if (file.progress !== 100) return [...acc, file];
        return acc;
      }, []);
    },
  },
  computed: {
    allFiles() {
      return [...this.files];
    },
  },
  methods: {
    deepCloneArray(array) {
      return JSON.parse(JSON.stringify(array));
    },
    // Remove an item from a passed array
    remove(arr, id) {
      return arr.filter(val => val !== id);
    },
    // Initiate file uploads
    handleFiles() {
      // Get the list of files from the input
      const { files } = this.$refs.attachments;
      // Loop over the files
      Object.values(files).map(file => {
        // If file size is over 15MB in binary byte representation
        if (file.size > MAX_FILE_SIZE_BYTES) {
          this.$refs.attachments.value = ''; // Reset the input
          this.$emit('error', file);
          return null;
        }

        // If the user has already uploaded a file with this name during this session
        if (this.allFiles.find(f => f.name === file.name)) {
          this.$refs.attachments.value = ''; // Reset the input
          return null;
        }

        // Add this file to the uploaded array
        const item = { name: file.name, type: file.type, size_bytes: file.size, progress: 0 };
        this.uploaded = [...this.uploaded, item];
        this.uploadFile(file, item);

        return null;
      });

      // need this to show upload progress on the UI
      this.$emit('change', this.deepCloneArray(this.uploaded));
      this.$refs.attachments.value = ''; // Reset the input
    },
    async uploadFile(file, item) {
      // Create new form to upload
      const formData = new FormData();
      formData.set('attachments', file);
      formData.set('folder_id', this.folderId);
      try {
        const response = await feathers
          .service('files')
          .create(formData, {
            connection: {
              // Track and update the file progress
              onUploadProgress: e => {
                const i = this.uploaded.findIndex(f => f.name === file.name);
                /* Progress is tracked and a 15% progress is deducted to allow for
                  the actual S3 upload in the back-end
                */
                item.progress = ((e.loaded / e.total) * 100) - 15;
                this.uploaded[i] = item;
              },
            },
          });
        const i = this.uploaded.findIndex(f => f.name === response.name);
        this.uploaded[i].progress = 100;
        this.uploaded[i].id = response.id;
        this.uploaded[i].path = response.path;
        this.$emit('complete', this.filterNoIds(this.uploaded));
      } catch (err) {
        this.$refs.attachments.value = ''; // Reset the input
        this.$emit('error', file);
      }
    },
    handleRemoveAttachment(filename) {
      const files = this.uploaded;
      const index = files.findIndex(f => f.name === filename);
      const updated = [...files.slice(0, index), ...files.slice(index + 1)];
      this.uploaded = updated;
      this.$emit('change', this.uploaded);
    },
    filterNoIds(uploaded) {
      return uploaded.filter(file => file.id);
    },
  },
};
</script>
